/**
 * Offline Queue Hook for Driver Document Uploads
 * 
 * Stores files in IndexedDB when offline, auto-syncs when back online.
 * Uses raw IndexedDB (no dependencies) for maximum reliability.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const DB_NAME = 'drayly_offline';
const DB_VERSION = 1;
const STORE_NAME = 'pending_uploads';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
  });
}

async function addToQueue(item) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.add(item);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function getAllQueued() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function removeFromQueue(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

async function clearQueue() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.clear();
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

export function useOfflineQueue(api) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);
  const syncingRef = useRef(false);
  const processQueueRef = useRef(null);
  const apiRef = useRef(api);

  // Keep refs current to avoid stale closures
  useEffect(() => { apiRef.current = api; }, [api]);

  // Track online/offline status (uses ref to avoid stale closure)
  useEffect(() => {
    const goOnline = () => {
      setIsOnline(true);
      toast.success('Back online — syncing pending uploads...');
      if (processQueueRef.current) processQueueRef.current();
    };
    const goOffline = () => {
      setIsOnline(false);
      toast.warning('You are offline. Files will be saved and sent when back online.');
    };

    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);

    // Load initial pending count
    refreshCount();

    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Periodic auto-sync: check every 30s if online with pending items
  useEffect(() => {
    const interval = setInterval(() => {
      if (navigator.onLine && !syncingRef.current) {
        getAllQueued().then(items => {
          if (items.length > 0 && processQueueRef.current) {
            processQueueRef.current();
          }
        }).catch(() => {});
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const refreshCount = useCallback(async () => {
    try {
      const items = await getAllQueued();
      setPendingCount(items.length);
    } catch {
      setPendingCount(0);
    }
  }, []);

  // Queue a file for upload when offline
  const queueFile = useCallback(async (file, receiverId, context = 'message') => {
    try {
      // Read file as ArrayBuffer for IndexedDB storage
      const arrayBuffer = await file.arrayBuffer();

      await addToQueue({
        fileName: file.name,
        fileType: file.type,
        fileSize: file.size,
        fileData: arrayBuffer,
        receiverId,
        context,
        queuedAt: new Date().toISOString(),
      });

      await refreshCount();
      return true;
    } catch (error) {
      console.error('Failed to queue file offline:', error);
      return false;
    }
  }, [refreshCount]);

  // Process queued uploads when back online
  const processQueue = useCallback(async () => {
    if (syncingRef.current || !navigator.onLine) return;
    syncingRef.current = true;
    setIsSyncing(true);

    const currentApi = apiRef.current;

    try {
      const items = await getAllQueued();
      if (items.length === 0) {
        setIsSyncing(false);
        syncingRef.current = false;
        return;
      }

      let successCount = 0;
      let failCount = 0;

      // Group items by receiverId for batch sending
      const grouped = {};
      for (const item of items) {
        const key = item.receiverId || 'unknown';
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(item);
      }

      for (const [receiverId, files] of Object.entries(grouped)) {
        const uploaded = [];

        for (const item of files) {
          try {
            // Reconstruct File from stored ArrayBuffer
            const blob = new Blob([item.fileData], { type: item.fileType });
            const formData = new FormData();
            formData.append('file', blob, item.fileName);

            const response = await currentApi.post('/messages/upload-attachment', formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });

            uploaded.push({
              file_id: response.data.file_id,
              filename: response.data.filename || item.fileName,
              is_image: item.fileType.startsWith('image/'),
              content_type: item.fileType,
              queueId: item.id,
            });
          } catch (error) {
            console.error(`Failed to upload queued file ${item.fileName}:`, error);
            failCount++;
          }
        }

        // Send message with all uploaded files
        if (uploaded.length > 0 && receiverId !== 'unknown') {
          try {
            const fileLabel = uploaded.length === 1
              ? (uploaded[0].is_image ? 'Photo from driver' : 'Document from driver')
              : `${uploaded.length} files from driver`;

            await currentApi.post('/messages/send', {
              receiver_id: receiverId,
              content: fileLabel,
              message_type: uploaded.every(u => u.is_image) ? 'image' : 'file',
              file_url: uploaded[0].file_id,
              attachments: uploaded.map(u => ({
                file_id: u.file_id,
                filename: u.filename,
                is_image: u.is_image,
                content_type: u.content_type,
              })),
            });

            // Remove successfully sent items from queue
            for (const u of uploaded) {
              await removeFromQueue(u.queueId);
              successCount++;
            }
          } catch (error) {
            console.error('Failed to send queued message:', error);
            failCount += uploaded.length;
          }
        } else if (uploaded.length > 0 && receiverId === 'unknown') {
          // Files uploaded but no receiver — remove from queue to avoid infinite retry
          for (const u of uploaded) {
            await removeFromQueue(u.queueId);
            successCount++;
          }
        }
      }

      await refreshCount();

      if (successCount > 0) {
        toast.success(`Synced ${successCount} offline file${successCount > 1 ? 's' : ''} successfully`);
      }
      if (failCount > 0) {
        toast.error(`${failCount} file${failCount > 1 ? 's' : ''} failed to sync. Will retry later.`);
      }
    } catch (error) {
      console.error('Queue processing error:', error);
    } finally {
      setIsSyncing(false);
      syncingRef.current = false;
    }
  }, [refreshCount]);

  // Keep processQueue ref up to date
  useEffect(() => { processQueueRef.current = processQueue; }, [processQueue]);

  // Retry sync manually
  const retrySync = useCallback(() => {
    if (navigator.onLine) {
      processQueue();
    } else {
      toast.warning('Still offline. Will sync automatically when back online.');
    }
  }, [processQueue]);

  // Clear all pending items
  const clearPending = useCallback(async () => {
    await clearQueue();
    await refreshCount();
    toast.info('Cleared all pending offline uploads');
  }, [refreshCount]);

  return {
    isOnline,
    pendingCount,
    isSyncing,
    queueFile,
    processQueue,
    retrySync,
    clearPending,
  };
}
